import { UserManager, WebStorageStateStore } from 'oidc-client';
import { AppCuesService } from 'modules/shared/AppCuesService';
import { IdentityProviderSelector } from './IdentityProviderSelector';
import { blipAccountUrl, blipAccountClientId, blipLoginUrl, blipLoginClientId, accountManagementAuthentication, } from '../../app.constants';
const MILLISECONDS_IN_SECOND = 1000;
const MINUTES_TO_MILISECONDS = 60000;
const MINUTES_TO_SECONDS = 60;
const ACTIVE_KEY = 'user_active';
const INACTIVITY_INFO_TRACKING = 'portal-inactivity-alert-info-clicked';
const INACTIVITY_WARNING_TRACKING = 'portal-inactivity-alert-warning-clicked';
export class OidcService {
    static $inject = ['AuthenticationService', '$state', 'BlipService', 'SegmentService', 'AlertService', '$translate', 'ACCESS_TOKEN_EXPIRING_MINUTES', 'IDLE_TIME_MINUTES', 'NON_EXPIRING_PAGES', 'BlipToastService'];
    constructor(AuthenticationService, $state, BlipService, SegmentService, AlertService, $translate, ACCESS_TOKEN_EXPIRING_MINUTES, IDLE_TIME_MINUTES, NON_EXPIRING_PAGES, BlipToastService) {
        'ngInject';
        this.AuthenticationService = AuthenticationService;
        this.$state = $state;
        this.BlipService = BlipService;
        this.SegmentService = SegmentService;
        this.AlertService = AlertService;
        this.$translate = $translate;
        this.ACCESS_TOKEN_EXPIRING_MINUTES = ACCESS_TOKEN_EXPIRING_MINUTES;
        this.IDLE_TIME_MINUTES = IDLE_TIME_MINUTES;
        this.NON_EXPIRING_PAGES = NON_EXPIRING_PAGES;
        this.BlipToastService = BlipToastService;
        this.providerChanged = false;
        this.signOutUser = () => {
            if (this.AuthenticationService.isAuthenticated()) {
                this.logoutUser();
            }
            this.clearOidcStorageData();
            if (this.$state && this.$state.params && !this.$state.params.preventRedirect) {
                this.$state.go('anon.login');
            }
        };
        this.setUserActive = (isActive) => {
            if (this.AuthenticationService.isAuthenticated()) {
                if (isActive) {
                    localStorage.setItem(ACTIVE_KEY, 'true');
                    this.lastActiveDate = undefined;
                }
                else {
                    this.lastActiveDate = new Date();
                    localStorage.removeItem(ACTIVE_KEY);
                }
            }
        };
        this.closeInactivityAlert = () => {
            this.inactivityAlert && this.inactivityAlert.controller.closeAlert();
            this.inactivityAlert = undefined;
        };
        this.logoutUser = () => {
            this.BlipService.close();
            AppCuesService.instance.user = undefined;
            this.SegmentService.resetAccount();
            this.AuthenticationService.logout();
        };
        this.clearOidcStorageData = () => {
            Object.keys(localStorage)
                .filter(key => key.startsWith('oidc.'))
                .forEach(key => localStorage.removeItem(key));
        };
        this.isIdle = () => {
            return !localStorage.getItem(ACTIVE_KEY) && !this.isNonExpirePageActive();
        };
        this.getIdleMinutes = () => {
            if (this.lastActiveDate) {
                const totalMinutes = ((new Date().getTime() - this.lastActiveDate.getTime()) / 60000);
                return (totalMinutes + parseInt(this.IDLE_TIME_MINUTES)).toFixed(2);
            }
        };
        this.isNonExpirePageActive = () => {
            return this.NON_EXPIRING_PAGES.some((page) => window.location.pathname.includes(page));
        };
        this.getInactivityAlertInfoTime = () => {
            return parseInt(this.IDLE_TIME_MINUTES) + parseInt(this.ACCESS_TOKEN_EXPIRING_MINUTES);
        };
        this.openInactivityAlertInfo = async () => {
            const inactivityAlertInfo = {
                title: this.$translate.instant('inactivityAlertInfo.title'),
                body: this.$translate.instant('inactivityAlertInfo.body', {
                    hours: this.minutesToHoursMinutes(this.getInactivityAlertInfoTime())[0],
                    minutes: this.minutesToHoursMinutes(this.getInactivityAlertInfoTime())[1],
                }),
                buttons: {
                    confirm: this.$translate.instant('inactivityAlertInfo.buttons.confirm'),
                },
            };
            this.inactivityAlert = await this.AlertService.show({
                title: inactivityAlertInfo.title,
                body: inactivityAlertInfo.body,
                variant: 'system',
                icon: 'true',
                buttons: {
                    confirm: inactivityAlertInfo.buttons.confirm,
                },
                callback: {
                    confirmButtonAction: () => {
                        this.SegmentService.createOrganizationTrack(INACTIVITY_INFO_TRACKING, { idleMinutes: this.getIdleMinutes() });
                        this.setUserActive(true);
                        this._userManager.startSilentRenew();
                        this.inactivityAlert = undefined;
                    },
                },
            });
        };
        this.getInactivityAlertWarningTime = () => {
            return parseInt(this.IDLE_TIME_MINUTES) + parseInt(this.ACCESS_TOKEN_EXPIRING_MINUTES) * 2;
        };
        this.minutesToHoursMinutes = (totalminutes) => {
            if (totalminutes <= 60) {
                return [0, totalminutes];
            }
            const hours = Math.floor(totalminutes / 60);
            const minutes = Math.floor(totalminutes % 60);
            return [hours, minutes];
        };
        this.openInactivityAlertWarning = async () => {
            const inactivityAlertWarning = {
                title: this.$translate.instant('inactivityAlertWarning.title'),
                body: this.$translate.instant('inactivityAlertWarning.body', { hours: this.minutesToHoursMinutes(this.getInactivityAlertWarningTime())[0] }),
                buttons: {
                    confirm: this.$translate.instant('inactivityAlertWarning.buttons.confirm'),
                },
            };
            this.closeInactivityAlert();
            this.inactivityAlert = await this.AlertService.show({
                title: inactivityAlertWarning.title,
                body: inactivityAlertWarning.body,
                variant: 'warning',
                icon: 'true',
                buttons: {
                    confirm: inactivityAlertWarning.buttons.confirm,
                },
                callback: {
                    confirmButtonAction: () => {
                        this.SegmentService.createOrganizationTrack(INACTIVITY_WARNING_TRACKING, { idleMinutes: this.getIdleMinutes() });
                        this.$state.reload();
                    },
                },
            });
        };
        this.validateChangedProvider = async (ISSUER) => {
            const authData = this.AuthenticationService.auth;
            if (authData?.issuer) {
                const currentIssuer = new URL(decodeURIComponent(authData.issuer));
                const newIssuer = new URL(ISSUER);
                const hasIssuerChanged = currentIssuer.origin !== newIssuer.origin;
                if (hasIssuerChanged && !this.providerChanged) {
                    this.providerChanged = true;
                    const sessionExpired = async () => {
                        this.AuthenticationService.logout();
                        this.logoutUser();
                        await this._userManager.signoutRedirect({
                            post_logout_redirect_uri: window.location.origin,
                        });
                        await this._userManager.removeUser();
                    };
                    const title = await this.$translate('invalidTokenDangerInfo.title');
                    const msg = await this.$translate('invalidTokenDangerInfo.body');
                    const buttonText = await this.$translate('invalidTokenDangerInfo.buttons.confirm');
                    const DURATION = 10000;
                    this.BlipToastService.show('danger', {
                        title,
                        msg,
                        buttonText,
                        callback: sessionExpired,
                        duration: DURATION,
                    });
                    setTimeout(sessionExpired, DURATION);
                    return true;
                }
            }
            return false;
        };
        this.initializedPromise = this.init();
    }
    getInitializedPromise() {
        return this.initializedPromise;
    }
    async init() {
        const currentSubDomain = `${window.location.origin}`; // Needs to be dynamic due to custom workspaces domains
        const providerData = await this.getProviderData();
        if (!this.providerChanged) {
            this.validateChangedProvider(providerData.metadata.issuer);
        }
        //Enable Oidc logger using browser console
        this._userManager = new UserManager({
            authority: providerData.authority.replace('http://', 'https://'),
            client_id: providerData.client_id,
            redirect_uri: providerData.redirect_uri,
            response_type: providerData.response_type,
            scope: providerData.scope,
            post_logout_redirect_uri: currentSubDomain,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            automaticSilentRenew: true,
            silent_redirect_uri: `${currentSubDomain}/renew-callback.html`,
            accessTokenExpiringNotificationTime: parseInt(this.ACCESS_TOKEN_EXPIRING_MINUTES) * MINUTES_TO_SECONDS,
            metadata: providerData.metadata,
        });
        let time;
        let lastUserEmail = this.AuthenticationService.auth?.email;
        this.setUserActive(true);
        const resetTimer = () => {
            if (!this.inactivityAlert) {
                clearTimeout(time);
                time = setTimeout(() => setIdleContext(true), parseInt(this.IDLE_TIME_MINUTES) * MINUTES_TO_MILISECONDS);
                if (this.isIdle()) {
                    setIdleContext(false);
                }
            }
        };
        window.addEventListener('storage', async (event) => {
            if (event.key == ACTIVE_KEY && event.oldValue != event.newValue) {
                setIdleContext(this.isIdle());
            }
        });
        window.addEventListener('load', resetTimer, true);
        const events = ['mousemove', 'mousedown', 'scroll', 'touchstart', 'click', 'keypress'];
        events.forEach((name) => {
            document.addEventListener(name, resetTimer, true);
        });
        const setIdleContext = async (idle) => {
            if (lastUserEmail && lastUserEmail != this.AuthenticationService.auth?.email) {
                this.signOutUser();
            }
            if (idle) {
                if (!this.isNonExpirePageActive()) {
                    this.setUserActive(false);
                    clearTimeout(time);
                    this._userManager.stopSilentRenew();
                }
            }
            else {
                this.setUserActive(true);
                this._userManager.startSilentRenew();
                clearTimeout(time);
                time = setTimeout(() => setIdleContext(true), parseInt(this.IDLE_TIME_MINUTES) * MINUTES_TO_MILISECONDS);
                this.closeInactivityAlert();
                if (!this.BlipService.isConnected()) {
                    if (!this.AuthenticationService.email) {
                        await this.AuthenticationService.waitForInitialization();
                    }
                    await this.BlipService.connect(encodeURIComponent(this.AuthenticationService.email), this.AuthenticationService.token);
                }
            }
        };
        this._userManager.events.addAccessTokenExpiring(() => {
            if (this.isIdle()) {
                this.openInactivityAlertInfo();
            }
        });
        this._userManager.events.addAccessTokenExpired(() => {
            if (this.isIdle()) {
                this.openInactivityAlertWarning();
                this.BlipService.close();
            }
        });
        this._userManager.events.addSilentRenewError((event) => {
            console.error('Error trying to silent signin', event);
        });
        this._userManager.events.addUserLoaded((user) => {
            const oidcData = localStorage.getItem('oidc.azureB2CData');
            const isEnabled = oidcData ? JSON.parse(oidcData).isEnabled : false;
            lastUserEmail = user.profile.email;
            this.AuthenticationService.auth = {
                ...(this.AuthenticationService.auth || {}),
                token: isEnabled ? user.id_token : user.access_token,
                tokenType: user.token_type,
                expires: user.expires_at * MILLISECONDS_IN_SECOND,
                email: user.profile.email,
                issuer: providerData.metadata.issuer,
            };
        });
        this._userManager.events.addUserSignedOut(() => {
            if (!this.inactivityAlert) {
                this.signOutUser();
            }
        });
    }
    setBlipLoginProvider() {
        const BLIP_LOGIN_URL = blipLoginUrl;
        const CLIENT_ID = blipLoginClientId;
        const AUTHORITY = `${BLIP_LOGIN_URL}/b2c_1a_blipsignupsignin`;
        const AUTHORITY_ENDPOINT = `${AUTHORITY}/oauth2/v2.0`;
        const REDIRECT_URI = `${location.origin}/authorize`;
        const AUTHORIZATION_ENDPOINT = `${accountManagementAuthentication}/redirect/login`;
        const blipLoginProviderData = {
            authority: AUTHORITY,
            client_id: CLIENT_ID,
            redirect_uri: REDIRECT_URI,
            response_type: 'code',
            scope: `openid profile email ${CLIENT_ID}`,
            metadata: {
                issuer: `${BLIP_LOGIN_URL}/v2.0/`,
                authorization_endpoint: AUTHORIZATION_ENDPOINT,
                userinfo_endpoint: `${AUTHORITY}/openid/v2.0/userinfo`,
                end_session_endpoint: `${AUTHORITY_ENDPOINT}/logout`,
                jwks_uri: `${AUTHORITY}/discovery/v2.0/keys`,
                token_endpoint: `${AUTHORITY_ENDPOINT}/token`,
            },
        };
        return blipLoginProviderData;
    }
    setBlipAccountProvider() {
        const BLIP_ACCOUNT_URL = blipAccountUrl;
        const blipAccountProviderData = {
            authority: BLIP_ACCOUNT_URL,
            client_id: blipAccountClientId,
            redirect_uri: `${location.origin}/authorize`,
            response_type: 'id_token token',
            scope: 'offline_access openid profile email api-payment-service.full_access api-extension-service.full_access',
            metadata: {
                issuer: BLIP_ACCOUNT_URL,
                authorization_endpoint: `${BLIP_ACCOUNT_URL}/connect/authorize`,
                userinfo_endpoint: `${BLIP_ACCOUNT_URL}/connect/userinfo`,
                end_session_endpoint: `${BLIP_ACCOUNT_URL}/connect/endsession`,
                jwks_uri: `${BLIP_ACCOUNT_URL}/.well-known/openid-configuration/jwks`,
                token_endpoint: `${BLIP_ACCOUNT_URL}/connect/token`,
                check_session_iframe: `${BLIP_ACCOUNT_URL}/connect/checksession`,
            },
        };
        return blipAccountProviderData;
    }
    async getProviderData() {
        const isAzureADB2CEnabled = await IdentityProviderSelector.getInstance().isBlipLoginEnabled();
        return isAzureADB2CEnabled ? this.setBlipLoginProvider() : this.setBlipAccountProvider();
    }
    get UserManager() {
        return this._userManager;
    }
}
OidcService.$inject = ['AuthenticationService', '$state', 'BlipService', 'SegmentService', 'AlertService', '$translate', 'ACCESS_TOKEN_EXPIRING_MINUTES', 'IDLE_TIME_MINUTES', 'NON_EXPIRING_PAGES', 'BlipToastService'];
